<template>
  <v-expansion-panels>
    <template v-for="(f, key) in field">
      <v-expansion-panel
        v-if="checkSomeKeyNotExists(f)"
        :key="key"
      >
        <v-expansion-panel-header
          :color="f.header_color ? f.header_color : ''"
        >
          <v-layout :align-center="true">
            <v-icon
              v-if="f.icon"
              :color="f.icon_color"
              class="mr-2"
            >{{f.icon}}</v-icon>

            {{returnFilterKeyOrLabel(null, f)}}
          </v-layout>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <template
                v-for="(item, key) in items(f)"
              >
                <tr
                  v-if="checkKeyNotExists(key, item)"
                  :key="key"
                  :class="{ 'audit-row-negative-score': isAuditTable && isNegativeScore(key) }"
                >
                  <td>
                    <v-btn
                      v-if="item.editable"
                      small
                      icon
                      @click="item.edit_toggle = !item.edit_toggle"
                      color="green"
                      class="mr-2"
                    >
                      <v-icon>mdi-pencil-outline</v-icon>
                    </v-btn>

                    <v-icon
                      v-if="item.icon"
                      :color="item.icon_color"
                      class="mr-2"
                    >{{item.icon}}</v-icon>


                    {{ returnLabelFromColumn(key, item) }}
                  </td>
                  <td>
                    <v-layout
                      :align-center="true"
                    >
                      <div
                        class="my-1"
                        v-show="item.edit_toggle"
                      >
                        <template
                          v-if="item.editable"
                        >
                          <!--STRING FIELDS-->
                          <v-text-field
                            v-if="['STRING'].includes(item.type)"
                            outlined
                            dense
                            v-model.trim="item_obj[key]"
                            @input="$emit('edit_obj', $event)"
                            :readonly="!modal_create_mode && !canSave"
                            hide-details="auto"
                            background-color="white"
                          />

                          <!--TEXTAREA FIELDS-->
                          <v-textarea
                            v-else-if="['TEXT'].includes(item.type)"
                            outlined
                            dense
                            v-model.trim="item_obj[key]"
                            @input="$emit('edit_obj', $event)"
                            auto-grow
                            rows="2"
                            :readonly="!modal_create_mode && !canSave"
                            hide-details="auto"
                            background-color="white"
                          />

                          <!-- INTEGER FIELDS -->
                          <v-text-field
                            v-else-if="['INTEGER', 'BIGINT'].includes(item.type)"
                            outlined
                            dense
                            v-model.trim="item_obj[key]"
                            @input="$emit('edit_obj', $event)"
                            :rules="[rules.onlyDigits]"
                            :readonly="!modal_create_mode && !canSave"
                            hide-details="auto"
                            background-color="white"
                          ></v-text-field>

                          <!-- BOOL FIELDS -->
                          <v-checkbox
                            v-else-if="['BOOLEAN'].includes(item.type)"
                            v-model="item_obj[key]"
                            @input="$emit('edit_obj', $event)"
                            class="mt-0 mb-4"
                            :readonly="!modal_create_mode && !canSave"
                            hide-details="auto"
                          ></v-checkbox>

                        </template>
                      </div>

                      <div
                        v-show="!item.edit_toggle"
                      >
                        <template v-if="isAuditTable">
                          <template v-if="item.type === 'FILE' && parseFileUrls(item_obj[key]).length > 0">
                            <v-sheet
                              v-if="parseFileUrls(item_obj[key]).length > 1"
                              class="py-1"
                              height="150"
                              max-height="150"
                            >
                              <CdImageSlider
                                :imageSliderObject="parseFileUrls(item_obj[key]).map((url, idx) => ({ url, id: idx }))"
                              />
                            </v-sheet>
                            <CdImageItem
                              v-else
                              :imageUrl="parseFileUrls(item_obj[key])[0]"
                              :key="key"
                              class="py-1"
                            />
                          </template>
                          <CdImageItem
                            v-else-if="isImageUrl(item_obj[key])"
                            :imageUrl="item_obj[key]"
                            :key="key"
                            class="py-1"
                          />
                          <template v-else>
                            <template v-if="item.type === 'BOOLEAN'">
                              <span v-if="item_obj[key] === 0 || item_obj[key] === '0'">{{ $t('modules.customdata.audit.no') }}</span>
                              <span v-else-if="item_obj[key] === 1 || item_obj[key] === '1'">{{ $t('modules.customdata.audit.yes') }}</span>
                            </template>
                            <template v-else>
                              <span v-if="item_obj[key] === 'full'">{{ $t('modules.customdata.audit.full') }}</span>
                              <span v-else-if="item_obj[key] === 'partial'">{{ $t('modules.customdata.audit.partial') }}</span>
                              <span v-else>{{ item_obj[key] }}</span>
                            </template>
                          </template>
                        </template>
                        <template v-else>{{ item_obj[key] }}</template>
                      </div>
                    </v-layout>

                  </td>
                </tr>
              </template>
              </tbody>
            </template>
          </v-simple-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </template>
  </v-expansion-panels>

</template>

<script>

import {mapGetters} from "vuex";
import Vue from "vue";
import axios from "axios";
import CdImageItem from "@/modules/CustomData/views/customdata/NewComponents/CdImageItem.vue";
import CdImageSlider from "@/modules/CustomData/views/customdata/NewComponents/CdImageSlider.vue";
import { isImageUrl, parseFileUrls } from "@/modules/CustomData/utils/image.js";
import { normalizeActivityName } from '@/modules/CustomData/utils/activityNormalize';

const AUDIT_TABLE_LIST_MODEL = 'audit_table_list';
const AUDIT_QUESTIONS_MODEL = 'audit_questions';

export default {
  name: 'ExpansionPanelField',

  components: {
    CdImageItem,
    CdImageSlider,
  },

  props: {
    modal_create_mode: {
      type: Boolean,
      default: false,
    },
    canSave: {
      type: Boolean,
      default: false,
    },


    field: {
      type: Array,
      default: () => [],
    },
    entity_fields: {
      type: Object,
      default: () => {},
    },
    headers: {
      type: Array,
      default: () => [],
    },
    item_obj: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      expanded: false,
      isAuditTable: false,
      auditQuestionsList: [],
    };
  },

  computed: {
    ...mapGetters([
      'rules',
      'engine_settings',
    ]),
  },

  async mounted() {
    const modelName = this.$route?.params?.model_name;
    if (!modelName) return;
    const rows = await this.getAuditTableList();
    this.isAuditTable = rows.some(row => row.audit_table_list_name === modelName);
    if (this.isAuditTable) {
      this.auditQuestionsList = await this.getAuditQuestionsList();
    }
  },

  methods: {
    isImageUrl,
    parseFileUrls,

    async getAuditTableList() {
      const token = Vue.$cookies.get('token_e');
      const engine_url = this.engine_settings?.active_url;
      if (!engine_url) return [];
      const url = `${engine_url}/kw/custom_modules/${AUDIT_TABLE_LIST_MODEL}/list`;
      try {
        const response = await axios({
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
          },
          url,
          params: {
            bot_id: this.$route?.params?.id,
          },
          data: {
            limit: 9999,
            offset: 0,
          },
        });
        if (response?.data?.status === 'success') {
          return response.data.data.rows || [];
        }
      } catch (err) {
        console.error('ExpansionPanelField: getAuditTableList', err);
        return [];
      }
      return [];
    },

    async getAuditQuestionsList() {
      const token = Vue.$cookies.get('token_e');
      const engine_url = this.engine_settings?.active_url;
      if (!engine_url) return [];
      const url = `${engine_url}/kw/custom_modules/${AUDIT_QUESTIONS_MODEL}/list`;
      try {
        const response = await axios({
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
          },
          url,
          params: {
            bot_id: this.$route?.params?.id,
          },
          data: {
            limit: 9999,
            offset: 0,
          },
        });
        if (response?.data?.status === 'success') {
          return response.data.data.rows || [];
        }
      } catch (err) {
        console.error('ExpansionPanelField: getAuditQuestionsList', err);
        return [];
      }
      return [];
    },

    parseAnswerVariants(answerVariantString, context = {}) {
      try {
        if (!answerVariantString) return [];
        const variants = JSON.parse(answerVariantString);
        if (Array.isArray(variants)) {
          return variants.map(variant => ({
            text: variant.text || '',
            score: variant.score,
            column: variant.column || null,
            value: variant.value !== undefined ? variant.value : null,
            activity: variant.activity ?? null,
          }));
        }
        return [];
      } catch (e) {
        const ctx = Object.keys(context).length ? context : { note: 'ExpansionPanelField, add context at call site' };
        const preview = typeof answerVariantString === 'string'
          ? (answerVariantString.length > 600 ? answerVariantString.slice(0, 600) + '...[truncated]' : answerVariantString)
          : String(answerVariantString);
        console.error('[ExpansionPanelField] Error parsing answer variants:', {
          context: ctx,
          errorMessage: e?.message,
          parsePosition: e?.message?.match(/position (\d+)/)?.[1],
          rawPreview: preview,
          rawLength: typeof answerVariantString === 'string' ? answerVariantString.length : 0,
        }, e);
        return [];
      }
    },

    matchesActivity(option, normalizedActivity) {
      if (!normalizedActivity) return !option.activity;
      if (Array.isArray(option.activity)) return option.activity.includes(normalizedActivity);
      return option.activity === normalizedActivity;
    },

    resolveVariantForScore(row, columnKey, savedVal) {
      const variants = this.parseAnswerVariants(row.audit_question_answer_variant, { questionId: row?.id, audit_question: row?.audit_question });
      const matching = variants.filter(v => {
        if (v.column !== columnKey) return false;
        if (v.value === savedVal || v.text === savedVal) return true;
        if (String(v.value) === String(savedVal) || String(v.text) === String(savedVal)) return true;
        if (v.value == savedVal || v.text == savedVal) return true;
        return false;
      });
      if (this.item_obj?.audit_mode === 'unified') {
        return matching.find(v => v.activity === undefined || v.activity === null) || matching[0];
      }
      const activityNormalized = this.item_obj?.activity_normalized
        || (this.item_obj?.activity_name ? normalizeActivityName(this.item_obj.activity_name) : '');
      if (activityNormalized) {
        const withActivity = matching.find(v => this.matchesActivity(v, activityNormalized));
        if (withActivity) return withActivity;
      }
      return matching.find(v => !v.activity) || matching[0];
    },

    getScoreForColumn(columnKey) {
      if (!this.auditQuestionsList?.length || !this.item_obj) return null;
      const savedVal = this.item_obj[columnKey];
      if (savedVal === undefined || savedVal === null) return null;
      const auditType = this.item_obj.audit_type || 'full';
      for (const row of this.auditQuestionsList) {
        const variant = this.resolveVariantForScore(row, columnKey, savedVal);
        if (variant && variant.score !== undefined) {
          let score = 0;
          if (typeof variant.score === 'object' && variant.score !== null && variant.score[auditType] !== undefined) {
            score = parseInt(variant.score[auditType], 10);
          } else {
            score = parseInt(variant.score, 10);
          }
          return isNaN(score) ? null : score;
        }
      }
      return null;
    },

    isNegativeScore(key) {
      const score = this.getScoreForColumn(key);
      return score !== null && score <= 0;
    },

    items(field) {

      //used for find keys by regex (wider in ExpansionPanelVariableField.vue)
      if(
        'findAs' in field
        && field?.findAs?.length
      ) {
        //'^supervisor_.+$';
        let regexStr = field?.findAs;
        let regex = new RegExp(regexStr);

        return Object.keys(this.entity_fields)
          .filter(key => regex.test(key))
          .reduce((obj, key) => {
            obj[key] = {
              type: this.entity_fields[key].type,
              label: this.entity_fields[key]?.label || key
            };
            return obj;
          }, {});
      }

      return field.items
    },


    checkKeyNotExists(key, field) {
      let show = true

      if(
        field?.hide_key_not_exists
        && !(key in this.item_obj)
      ) show = false

      return show
    },
    checkSomeKeyNotExists(f) {
      let show = true

      if(f?.hide_some_key_not_exists) {
        const items = Object.keys(f.items).filter((i) => {
          if(this.checkKeyNotExists(i, f.items[i])) {
            return i
          }
        })

        if(!items?.length) show = false
      }

      return show
    },
    returnLabelFromColumn(key, item = null) {
      let label = this.entity_fields[key]?.label || key
      let label_from_column_name = this.entity_fields?.[item?.label_from_column_name]?.label

      //get label from headers(columns in list)
      if(item?.label_from_column_name) {
        label_from_column_name =  this.headers?.find((i) => i.value === item.label_from_column_name )?.text
      }

      return item?.label || label_from_column_name || label
    },

    returnFilterKeyOrLabel(key, item) {
      const label_from_column_name = this.entity_fields?.[item?.label_from_column_name]?.label

      return item?.label || label_from_column_name || key
    },
  },

}

</script>

<style scoped lang="scss">
.audit-row-negative-score td {
  background-color: #ffebee;
  color: #b71c1c;
}
</style>